import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  Alert,
  Autocomplete,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Link,
} from "@mui/material";
import PropTypes from "prop-types";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  getSiteAlertsQuery,
  postAlarmAction,
  deleteAlarmAction,
  putAlarmAction,
  getPagerDutyCanopyIntegrations,
  getSitePagerDutyServicesQuery,
} from "../../lib/Queries";
import { useSnackbar } from "notistack";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const IntegrationWarning = ({ message, onClose, onNavigateToIntegrations }) => (
  <Alert severity="warning" sx={{ mt: 1 }}>
    <Typography component="span" variant="body2">
      {message}{" "}
      <Link
        component="button"
        type="button"
        underline="always"
        onClick={() => {
          onClose();
          onNavigateToIntegrations?.();
        }}
        sx={{
          verticalAlign: "baseline",
          p: 0,
          border: "none",
          background: "none",
          cursor: "pointer",
          font: "inherit",
        }}
      >
        Integrations tab
      </Link>
      .
    </Typography>
  </Alert>
);

IntegrationWarning.propTypes = {
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onNavigateToIntegrations: PropTypes.func,
};

const AlarmActionsModal = ({
  open,
  onClose,
  mode = "new",
  action,
  assets = [],
  siteUuid,
  upkeepLocationId,
  onNavigateToIntegrations,
}) => {
  const title = mode === "edit" ? "Edit Alarm Action" : "New Alarm Action";

  const [selectedDevices, setSelectedDevices] = useState([]);
  const [selectedAlarms, setSelectedAlarms] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedPdService, setSelectedPdService] = useState(null);
  const [actionName, setActionName] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const safeAssets = Array.isArray(assets) ? assets : [];

  const selectedAssemblies = useMemo(
    () => (selectedDevices || []).map((d) => d?.value).filter(Boolean),
    [selectedDevices],
  );

  const { data: alertStrings = [], refetch } = useQuery({
    ...getSiteAlertsQuery(siteUuid, selectedAssemblies),
    enabled: Boolean(siteUuid),
  });

  const getAssetNameFromUUID = (uuid) => safeAssets.find((a) => a.uuid === uuid)?.name;

  const actionOptionsMap = {
    pagerduty: "Send to PagerDuty",
    upkeep: "Send to UpKeep",
  };

  const resetForm = () => {
    setSelectedDevices([]);
    setSelectedAlarms([]);
    setSelectedTask(null);
    setSelectedPdService(null);
    setActionName("");
  };

  const createMutation = useMutation({
    mutationFn: (body) => postAlarmAction(body),
    onSuccess: () => {
      enqueueSnackbar("Alarm Action created.", { autoHideDuration: 3000, variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["site-alarm-actions", siteUuid] });
      onClose();
    },
    onError: (err) => {
      enqueueSnackbar("Failed to create Alarm Action.", {
        autoHideDuration: 4000,
        variant: "error",
      });
    },
  });

  const saveMutation = useMutation({
    mutationFn: ({ uuid, body }) => putAlarmAction(uuid, body),
    onSuccess: () => {
      enqueueSnackbar("Alarm Action updated.", { autoHideDuration: 3000, variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["site-alarm-actions", siteUuid] });
      onClose();
    },
    onError: () => {
      enqueueSnackbar("Failed to update Alarm Action.", {
        autoHideDuration: 4000,
        variant: "error",
      });
    },
  });

  const isPagerDutyTask = open && selectedTask?.value === "pagerduty";

  const { data: configuredServices = [], isLoading: isCfgLoading } = useQuery({
    queryKey: ["pd-configured"],
    queryFn: getPagerDutyCanopyIntegrations,
    enabled: isPagerDutyTask,
  });

  const { data: sitePdServices } = useQuery({
    ...getSitePagerDutyServicesQuery(siteUuid),
    enabled: isPagerDutyTask && Boolean(siteUuid),
  });

  const siteEnabledIds = useMemo(
    () => new Set(sitePdServices?.pagerduty_service_ids || []),
    [sitePdServices],
  );

  const pdOptions = useMemo(() => {
    return (configuredServices || [])
      .filter((s) => siteEnabledIds.has(s.id))
      .map((s) => ({
        value: s.id,
        label: s.name,
      }));
  }, [configuredServices, siteEnabledIds]);

  const deleteMutation = useMutation({
    mutationFn: (uuid) => deleteAlarmAction(uuid),
    onSuccess: () => {
      enqueueSnackbar("Alarm Action deleted.", { autoHideDuration: 3000, variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["site-alarm-actions", siteUuid] });
      setShowDeleteConfirm(false);
      onClose();
    },
    onError: () => {
      enqueueSnackbar("Failed to delete Alarm Action.", {
        autoHideDuration: 4000,
        variant: "error",
      });
      setShowDeleteConfirm(false);
    },
  });

  const buildBody = () => ({
    site_uuid: siteUuid,
    action_name: actionName,
    action_task: selectedTask?.value,
    assemblies_uuids: selectedAssemblies,
    alerts: (selectedAlarms || []).map((a) => a?.value).filter(Boolean),
    ...(selectedTask?.value === "pagerduty" && selectedPdService?.value
      ? { action_config: { integration_id: selectedPdService.value } }
      : {}),
    ...(selectedTask?.value === "upkeep" && upkeepLocationId
      ? { action_config: { location_id: upkeepLocationId } }
      : {}),
  });

  const handleCreate = () => {
    const requiresPd = selectedTask?.value === "pagerduty";
    const hasPd = Boolean(selectedPdService?.value);
    if (
      !siteUuid ||
      !actionName ||
      !selectedTask?.value ||
      selectedAssemblies.length === 0 ||
      (requiresPd && !hasPd) ||
      (selectedTask?.value === "upkeep" && !(upkeepLocationId && String(upkeepLocationId).trim()))
    ) {
      enqueueSnackbar("Please fill out action name, task, and at least one device.", {
        autoHideDuration: 4000,
        variant: "warning",
      });
      if (requiresPd && !hasPd) {
        enqueueSnackbar("Please select a PagerDuty Service.", {
          autoHideDuration: 4000,
          variant: "warning",
        });
      }
      return;
    }
    createMutation.mutate(buildBody());
  };

  const handleSave = () => {
    const requiresPd = selectedTask?.value === "pagerduty";
    const hasPd = Boolean(selectedPdService?.value);
    if (
      !siteUuid ||
      !action?.action_uuid ||
      !actionName ||
      !selectedTask?.value ||
      selectedAssemblies.length === 0 ||
      (requiresPd && !hasPd) ||
      (selectedTask?.value === "upkeep" && !(upkeepLocationId && String(upkeepLocationId).trim()))
    ) {
      enqueueSnackbar("Please fill out action name, task, and at least one device.", {
        autoHideDuration: 4000,
        variant: "warning",
      });
      if (requiresPd && !hasPd) {
        enqueueSnackbar("Please select a PagerDuty Service.", {
          autoHideDuration: 4000,
          variant: "warning",
        });
      }
      return;
    }
    saveMutation.mutate({ uuid: action.action_uuid, body: buildBody() });
  };

  const handleDeleteAction = () => {
    if (!action?.action_uuid) return;
    deleteMutation.mutate(action.action_uuid);
  };

  useEffect(() => {
    // Refetch alerts when selected assemblies change
    if (siteUuid) {
      refetch();
    }
  }, [siteUuid, selectedAssemblies, refetch]);

  useEffect(() => {
    // If closed, always clear state so next open starts fresh
    if (!open) {
      resetForm();
      setShowDeleteConfirm(false);
      return;
    }

    // When opening in new mode, ensure clean slate
    if (mode === "new") {
      resetForm();
      setShowDeleteConfirm(false);
      return;
    }

    // When opening in edit mode with an action, populate fields
    if (mode === "edit" && action) {
      setActionName(action?.action_name || "");
      const devices = (action?.assemblies || []).map((a) => ({
        label: getAssetNameFromUUID(a.uuid) || a.uuid,
        value: a.uuid,
      }));
      setSelectedDevices(devices);
      const alarms = Array.from(
        new Set((action?.assemblies || []).flatMap((a) => a.alerts_list || [])),
      ).map((al) => ({ label: al, value: al }));
      setSelectedAlarms(alarms);
      setSelectedTask(
        action?.action_task
          ? {
              label: actionOptionsMap[action.action_task] || action.action_task,
              value: action.action_task,
            }
          : null,
      );
    }
  }, [open, mode, action]);

  // When editing a PagerDuty action, preselect the saved service based on action_config.integration_id
  useEffect(() => {
    if (!open || mode !== "edit" || !action) return;
    if (selectedTask?.value !== "pagerduty") return;
    const integrationId = action?.action_config?.integration_id;
    if (!integrationId) return;
    if (!pdOptions || pdOptions.length === 0) return;
    const match = pdOptions.find((o) => o.value === integrationId);
    if (match) setSelectedPdService(match);
  }, [open, mode, action, selectedTask, pdOptions]);

  const deviceOptions = useMemo(
    () =>
      safeAssets.map((a) => {
        return { value: a.uuid, label: a.name };
      }),
    [safeAssets],
  );
  const alarmOptions = useMemo(
    () => (alertStrings || []).map((s) => ({ value: s, label: s })),
    [alertStrings],
  );
  const taskOptions = Object.keys(actionOptionsMap).map((key) => {
    return { value: key, label: actionOptionsMap[key] };
  });

  const savedPdIntegrationId = action?.action_config?.integration_id;
  const savedPdServiceName =
    configuredServices.find((service) => service.id === savedPdIntegrationId)?.name || null;
  const isCreating = createMutation.isLoading;
  const isSaving = saveMutation.isLoading;
  const hasSelectedAlarms = (selectedAlarms || []).length > 0;
  const requiresPd = selectedTask?.value === "pagerduty";
  const hasPd = Boolean(selectedPdService?.value);
  const requiresUpkeepLocation = selectedTask?.value === "upkeep";
  const hasUpkeepLocation = Boolean(upkeepLocationId && String(upkeepLocationId).trim());
  const showMissingPdServiceWarning =
    mode === "edit" &&
    requiresPd &&
    Boolean(savedPdIntegrationId) &&
    !selectedPdService?.value &&
    !pdOptions.some((opt) => opt.value === savedPdIntegrationId);
  const disableCreate =
    isCreating ||
    !siteUuid ||
    !actionName ||
    !selectedTask?.value ||
    selectedAssemblies.length === 0 ||
    (requiresPd && !hasPd) ||
    (requiresUpkeepLocation && !hasUpkeepLocation);
  const disableSave =
    isSaving ||
    !siteUuid ||
    !action?.action_uuid ||
    !actionName ||
    !selectedTask?.value ||
    selectedAssemblies.length === 0 ||
    (requiresPd && !hasPd) ||
    (requiresUpkeepLocation && !hasUpkeepLocation);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth={false}
        PaperProps={{
          sx: {
            width: 600,
            maxWidth: "90vw",
            height: 680,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, ml: 1 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", ml: 1, mb: 2 }}>
            Alarm Actions are globally shared among users.
          </Typography>

          <Box sx={{ mt: 2, ml: 1, mr: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
              Trigger
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
              If any of these devices...
            </Typography>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={deviceOptions}
              value={selectedDevices}
              onChange={(_, v) => setSelectedDevices(v)}
              getOptionLabel={(o) => o?.label || ""}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              renderInput={(params) => <TextField {...params} label="Devices" size="small" />}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.label}
                  </li>
                );
              }}
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
              ...sends any of these alarms...
            </Typography>
            <Autocomplete
              disableCloseOnSelect
              options={alarmOptions}
              value={selectedAlarms}
              onChange={(_, v) => setSelectedAlarms(v)}
              multiple
              noOptionsText={
                alarmOptions.length === 0 && !hasSelectedAlarms
                  ? "No alarms apply to all selected assets"
                  : ""
              }
              getOptionLabel={(o) => o?.label || ""}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Alarm"
                  size="small"
                  placeholder={
                    alarmOptions.length === 0 && !hasSelectedAlarms
                      ? "No alarms apply to all selected assets"
                      : ""
                  }
                />
              )}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.label}
                  </li>
                );
              }}
            />
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
              ...then take this action:
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: "bold" }}>
              Action
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="action-select-label">Action</InputLabel>
              <Select
                labelId="action-select-label"
                id="action-select"
                label="Action"
                value={selectedTask?.value || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  const opt = taskOptions.find((o) => o.value === v) || null;
                  setSelectedTask(opt);
                }}
              >
                {taskOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedTask?.value === "upkeep" && !hasUpkeepLocation && (
              <IntegrationWarning
                message="To create Alarm Actions using UpKeep, enter an UpKeep Location ID for this site in the"
                onClose={onClose}
                onNavigateToIntegrations={onNavigateToIntegrations}
              />
            )}
            {selectedTask?.value === "pagerduty" && (
              <>
                <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                  <InputLabel id="pd-service-select-label">PagerDuty Service</InputLabel>
                  <Select
                    labelId="pd-service-select-label"
                    id="pd-service-select"
                    label="PagerDuty Service"
                    value={selectedPdService?.value || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      const opt = pdOptions.find((o) => o.value === v) || null;
                      setSelectedPdService(opt);
                    }}
                    disabled={isCfgLoading || pdOptions.length === 0}
                  >
                    {pdOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {showMissingPdServiceWarning && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {savedPdServiceName
                      ? `This action's PagerDuty service, "${savedPdServiceName}", is no longer enabled for this site. Re-enable it in Site Integrations or select a different service to save changes.`
                      : "This action's PagerDuty service is no longer enabled for this site. Re-enable it in Site Integrations or select a different service to save changes."}
                  </Alert>
                )}
                {!isCfgLoading && pdOptions.length === 0 && !showMissingPdServiceWarning && (
                  <IntegrationWarning
                    message="To create Alarm Actions using PagerDuty, add a PagerDuty service to this site in the"
                    onClose={onClose}
                    onNavigateToIntegrations={onNavigateToIntegrations}
                  />
                )}
              </>
            )}
            <TextField
              label="Action Name"
              variant="outlined"
              size="small"
              sx={{ width: "100%", mt: 2 }}
              value={actionName}
              onChange={(e) => setActionName(e.target.value)}
            />
          </Box>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: "auto", p: 4, justifyContent: "space-between" }}
        >
          {mode === "edit" ? (
            <Button color="error" variant="outlined" onClick={() => setShowDeleteConfirm(true)}>
              Delete Action
            </Button>
          ) : (
            <span />
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" color="textIcon" onClick={onClose}>
              Cancel
            </Button>
            {mode === "edit" ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={disableSave}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreate}
                disabled={disableCreate}
              >
                {isCreating ? "Creating..." : "Create Action"}
              </Button>
            )}
          </Box>
        </Stack>
      </Dialog>

      <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
        <Box sx={{ p: 3, width: 380 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Delete Alarm Action
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Deleting this alarm action will immediately stop the configured actions. This change
            applies to all users.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
            <Button variant="outlined" color="textIcon" onClick={() => setShowDeleteConfirm(false)}>
              Don&apos;t Delete
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteAction}
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? "Deleting..." : "Delete Action"}
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </>
  );
};

AlarmActionsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(["new", "edit"]),
  action: PropTypes.shape({
    action_uuid: PropTypes.string,
    site_uuid: PropTypes.string,
    action_name: PropTypes.string,
    action_task: PropTypes.string,
    assemblies: PropTypes.arrayOf(
      PropTypes.shape({
        uuid: PropTypes.string,
        alerts_list: PropTypes.arrayOf(PropTypes.string),
      }),
    ),
    action_config: PropTypes.shape({
      integration_id: PropTypes.string,
      location_id: PropTypes.string,
    }),
  }),
  assets: PropTypes.array,
  siteUuid: PropTypes.string,
  upkeepLocationId: PropTypes.string,
  onNavigateToIntegrations: PropTypes.func,
};

export default AlarmActionsModal;
